import { LIMITS, MAX_SOUND_SAMPLES, NOTE_CUT, RATE_CHOICES, TrackerSound, brrBytes, decodeSamples, encodeSamples, finalizeSound, maxNoteFor, newTrackerId, noteName } from "../../shared/tracker";
import { READY_MADE, SHAPES, SynthParams, lowerRate, paramsFor, readyMadeSound, soundFromMade, surprise, synthesize, toEightBit } from "../../shared/tracker_synth";
import { byId, flashToast } from "../dom";
import { t } from "../i18n";
import { previewNote } from "../tracker_player";
import { edit, state } from "../tracker_store";

// the sounds tab: every sound as a card with its waveform, trim and loop, plus the three ways to get one: make it, pick a
// ready made one, or import a .wav. the sound maker works on a copy and only adds to the song when asked

const EAR_KEY = "chi-tracker-ear";
let earCache = readEar();
let params: SynthParams = { ...paramsFor(READY_MADE.find((preset) => preset.id === "coin")!) };
let presetName = "Coin";
let genOpen = false;

function readEar(): boolean {
  try {
    return localStorage.getItem(EAR_KEY) === "1";
  } catch {
    return false;
  }
}

export const earOn = (): boolean => earCache;

const kb = (bytes: number): string => (bytes / 1000).toFixed(1);

// --- waveforms

function drawWave(canvas: HTMLCanvasElement, samples: Int8Array, overlay?: { start: number; end: number; loop: boolean; loopStart: number; loopEnd: number }): void {
  const context = canvas.getContext("2d");
  if (!context) return;
  const { width, height } = canvas;
  context.fillStyle = "#02060a";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "rgba(205,220,255,0.12)";
  context.beginPath();
  context.moveTo(0, height / 2);
  context.lineTo(width, height / 2);
  context.stroke();
  if (!samples.length) return;
  const columnAt = (index: number): number => Math.round((index / samples.length) * width);
  if (overlay?.loop) {
    context.fillStyle = "rgba(169,155,255,0.22)";
    context.fillRect(columnAt(overlay.loopStart), 0, Math.max(2, columnAt(overlay.loopEnd) - columnAt(overlay.loopStart)), height);
  }
  context.strokeStyle = "#4de8ff";
  context.lineWidth = 1;
  context.beginPath();
  for (let x = 0; x < width; x++) {
    const from = Math.floor((x / width) * samples.length);
    const to = Math.max(from + 1, Math.floor(((x + 1) / width) * samples.length));
    let low = 127;
    let high = -128;
    for (let i = from; i < to && i < samples.length; i++) {
      low = Math.min(low, samples[i]);
      high = Math.max(high, samples[i]);
    }
    context.moveTo(x + 0.5, height / 2 - (high / 128) * (height / 2 - 2));
    context.lineTo(x + 0.5, height / 2 - (low / 128) * (height / 2 - 2) + 1);
  }
  context.stroke();
  if (overlay) {
    context.fillStyle = "rgba(0,0,0,0.6)";
    context.fillRect(0, 0, columnAt(overlay.start), height);
    context.fillRect(columnAt(overlay.end), 0, width - columnAt(overlay.end), height);
  }
}

// --- adding and removing

function canAdd(): boolean {
  const song = state.song!;
  if (song.sounds.length >= LIMITS[song.mode].sounds) {
    flashToast(t("that is the most sounds this mode takes ({n})", { n: LIMITS[song.mode].sounds }));
    return false;
  }
  return true;
}

export function addSound(sound: TrackerSound): void {
  if (!state.song || !canAdd()) return;
  edit((song) => {
    song.sounds.push(sound);
    state.pen = song.sounds.length - 1;
  }, "sounds");
  const bytes = brrBytes(finalizeSound(sound).data.length);
  flashToast(t("{name} added, {kb} KB", { name: sound.name, kb: kb(bytes) }));
}

function usedBy(index: number): number {
  let count = 0;
  state.song!.patterns.forEach((pattern) => pattern.notes.forEach((note) => {
    if (note.n !== NOTE_CUT && note.s === index) count++;
  }));
  return count;
}

function removeSound(index: number): void {
  const used = usedBy(index);
  if (used && !confirm(t("{n} notes use this sound and will be cleared. remove it?", { n: used }))) return;
  edit((song) => {
    song.sounds.splice(index, 1);
    for (const pattern of song.patterns) {
      pattern.notes = pattern.notes.filter((note) => note.n === NOTE_CUT || note.s !== index);
      for (const note of pattern.notes) if (note.n !== NOTE_CUT && note.s > index) note.s--;
    }
    state.pen = Math.max(0, Math.min(state.pen, song.sounds.length - 1));
  }, "all");
}

// --- the cards

function slider(label: string, min: number, max: number, value: number, show: (value: number) => string, step = 1): { row: HTMLLabelElement; input: HTMLInputElement; out: HTMLOutputElement } {
  const row = document.createElement("label");
  row.className = "tk-slider";
  const input = document.createElement("input");
  input.type = "range";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  const out = document.createElement("output");
  out.textContent = show(value);
  input.addEventListener("input", () => {
    out.textContent = show(Number(input.value));
  });
  row.append(document.createTextNode(label), input, out);
  return { row, input, out };
}

function card(sound: TrackerSound, index: number): HTMLElement {
  const song = state.song!;
  const holder = document.createElement("div");
  holder.className = "tk-sound";
  const length = decodeSamples(sound.data).length;

  const head = document.createElement("div");
  head.className = "tk-sound-head";
  const name = document.createElement("input");
  name.value = sound.name;
  name.maxLength = 24;
  name.setAttribute("aria-label", t("sound name"));
  const cost = document.createElement("span");
  cost.className = "tk-kb";
  const play = document.createElement("button");
  play.type = "button";
  play.className = "pill icon-only";
  play.setAttribute("aria-label", t("play this sound"));
  play.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M7 4l13 8-13 8z"/></svg>';
  head.append(name, cost, play);

  const canvas = document.createElement("canvas");
  canvas.className = "tk-wave";
  canvas.width = 600;
  canvas.height = 112;
  canvas.setAttribute("aria-label", t("waveform of {name}", { name: sound.name }));

  const info = document.createElement("div");
  info.className = "tk-used";
  const refresh = (): void => {
    const own = song.sounds[index];
    if (!own) return;
    const final = finalizeSound(own);
    cost.textContent = `${kb(brrBytes(final.data.length))} KB`;
    drawWave(canvas, decodeSamples(own.data), { start: own.trimStart, end: own.trimEnd, loop: own.loop, loopStart: own.loopStart, loopEnd: own.loopEnd });
    const used = usedBy(index);
    info.textContent = t("{rate} Hz · highest key {note} · used by {n} notes", { rate: own.rate, note: noteName(maxNoteFor(own.rate)), n: used });
  };
  refresh();

  const change = (apply: (own: TrackerSound) => void, key: string): void => {
    edit((draft) => apply(draft.sounds[index]), "quiet", `${key}${index}`);
    refresh();
  };

  name.addEventListener("input", () => change((own) => void (own.name = name.value.slice(0, 24) || "sound"), "name"));
  play.addEventListener("click", () => previewNote(song.sounds[index], 60, earCache));

  const details = document.createElement("details");
  const summary = document.createElement("summary");
  summary.textContent = t("trim, loop and volume");
  details.append(summary);
  const tools = document.createElement("div");
  tools.className = "tk-block";
  const start = slider(t("start"), 0, length - 1, sound.trimStart, String);
  const end = slider(t("end"), 1, length, sound.trimEnd, String);
  const loopRow = document.createElement("label");
  loopRow.className = "tk-check";
  const loopBox = document.createElement("input");
  loopBox.type = "checkbox";
  loopBox.checked = sound.loop;
  loopRow.append(loopBox, document.createTextNode(t("repeat part of it while a note is held (a loop)")));
  const loopStart = slider(t("loop from"), 0, length - 1, sound.loopStart, String);
  const loopEnd = slider(t("loop to"), 1, length, sound.loopEnd, String);
  const volume = slider(t("volume"), 0, 64, sound.volume, String);
  const loopHint = document.createElement("p");
  loopHint.className = "hint";
  loopHint.textContent = t("loops snap to steps of 16 samples because the SNES needs that");
  const toggleLoop = (): void => {
    loopStart.row.hidden = !loopBox.checked;
    loopEnd.row.hidden = !loopBox.checked;
    loopHint.hidden = !loopBox.checked;
  };
  toggleLoop();
  start.input.addEventListener("input", () => change((own) => void (own.trimStart = Math.min(Number(start.input.value), own.trimEnd - 1)), "trim"));
  end.input.addEventListener("input", () => change((own) => void (own.trimEnd = Math.max(Number(end.input.value), own.trimStart + 1)), "trim"));
  loopBox.addEventListener("change", () => {
    toggleLoop();
    change((own) => {
      own.loop = loopBox.checked;
      if (own.loop && own.loopEnd <= own.loopStart) own.loopEnd = own.trimEnd;
    }, "loop");
  });
  loopStart.input.addEventListener("input", () => change((own) => void (own.loopStart = Math.min(Number(loopStart.input.value), own.loopEnd - 1)), "loopat"));
  loopEnd.input.addEventListener("input", () => change((own) => void (own.loopEnd = Math.max(Number(loopEnd.input.value), own.loopStart + 1)), "loopat"));
  volume.input.addEventListener("input", () => change((own) => void (own.volume = Number(volume.input.value)), "volume"));
  tools.append(start.row, end.row, loopRow, loopStart.row, loopEnd.row, loopHint, volume.row);

  const lower = RATE_CHOICES.filter((rate) => rate < sound.rate);
  if (lower.length) {
    const field = document.createElement("label");
    field.className = "field";
    field.textContent = t("record at a lower quality to save memory");
    const select = document.createElement("select");
    select.append(new Option(t("{rate} Hz now", { rate: sound.rate }), ""));
    for (const rate of lower) select.append(new Option(`${rate} Hz`, String(rate)));
    select.addEventListener("change", () => {
      const rate = Number(select.value);
      if (!rate) return;
      edit((draft) => void (draft.sounds[index] = lowerRate(draft.sounds[index], rate)), "sounds");
    });
    field.append(select);
    tools.append(field);
  }
  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "pill danger";
  remove.textContent = t("remove this sound");
  remove.addEventListener("click", () => removeSound(index));
  tools.append(remove);
  details.append(tools);
  holder.append(head, canvas, info, details);
  return holder;
}

export function renderSounds(): void {
  const song = state.song;
  if (!song) return;
  const holder = byId("tk-sounds");
  holder.replaceChildren(...song.sounds.map(card));
  byId("tk-sounds-empty").hidden = song.sounds.length > 0;
}

// --- importing .wav

async function decode(file: File): Promise<AudioBuffer> {
  const context = new OfflineAudioContext(1, 1, 44100);
  return context.decodeAudioData(await file.arrayBuffer());
}

// big recordings are recorded slower so they stay a sensible size: aim for about 12 KB once compressed
function pickRate(seconds: number, from: number): number {
  const want = 21_000 / Math.max(0.05, seconds);
  const choices = RATE_CHOICES.filter((rate) => rate <= from);
  const fit = [...choices].reverse().find((rate) => rate <= Math.max(8000, want));
  return fit || choices[0] || RATE_CHOICES[0];
}

async function importFiles(files: File[]): Promise<void> {
  for (const file of files) {
    if (!state.song || !canAdd()) return;
    try {
      const buffer = await decode(file);
      const mono = new Float32Array(buffer.length);
      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const data = buffer.getChannelData(channel);
        for (let i = 0; i < data.length; i++) mono[i] += data[i] / buffer.numberOfChannels;
      }
      let rate = pickRate(buffer.duration, buffer.sampleRate);
      let samples = toEightBit(mono, buffer.sampleRate, rate);
      while (samples.length > MAX_SOUND_SAMPLES && rate > 2000) {
        rate = Math.max(2000, Math.floor(rate / 2));
        samples = toEightBit(mono, buffer.sampleRate, rate);
      }
      addSound({
        id: newTrackerId(),
        name: file.name.replace(/\.[^.]+$/, "").slice(0, 24) || "sound",
        rate,
        data: encodeSamples(samples),
        trimStart: 0,
        trimEnd: samples.length,
        loop: false,
        loopStart: 0,
        loopEnd: samples.length,
        volume: 64
      });
    } catch {
      flashToast(t("could not read {name}, try a .wav file", { name: file.name }));
    }
  }
}

// --- the sound maker

function sliderSpec(): Array<{ label: string; key: keyof SynthParams; min: number; max: number; step?: number; show: (value: number) => string; only?: (current: SynthParams) => boolean }> {
  const percent = (value: number): string => `${value}%`;
  return [
    { label: t("pitch"), key: "freq", min: 60, max: 4000, step: 10, show: (value) => `${value} Hz`, only: (current) => current.kind === "effect" },
    { label: t("pitch slide"), key: "slide", min: -100, max: 100, show: String, only: (current) => current.kind === "effect" },
    { label: t("length"), key: "length", min: 20, max: 1200, step: 10, show: (value) => `${(value / 1000).toFixed(2)} s`, only: (current) => current.kind === "effect" },
    { label: t("fade out"), key: "fade", min: 0, max: 100, show: percent, only: (current) => current.kind === "effect" },
    { label: t("square width"), key: "duty", min: 10, max: 50, show: (value) => `${value}%`, only: (current) => current.shape === "square" },
    { label: t("crunch"), key: "crunch", min: 0, max: 100, show: percent },
    { label: t("softness"), key: "smooth", min: 0, max: 100, show: percent }
  ];
}

function chips(holder: HTMLElement, items: Array<{ label: string; on: boolean; hint?: string; run: () => void }>): void {
  holder.replaceChildren(...items.map((item) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "tk-chip";
    chip.textContent = item.label;
    if (item.hint) chip.title = item.hint;
    chip.setAttribute("aria-pressed", String(item.on));
    chip.addEventListener("click", item.run);
    return chip;
  }));
}

function renderGenerator(): void {
  byId("tk-gen").hidden = !genOpen;
  if (!genOpen) return;
  chips(byId("tk-gen-kind"), [
    { label: t("melody sound"), on: params.kind === "tone", hint: t("loops, so it plays any key and costs almost no memory"), run: () => void ((params = { ...params, kind: "tone", shape: params.shape === "noise" ? "square" : params.shape }), renderGenerator()) },
    { label: t("sound effect"), on: params.kind === "effect", hint: t("plays once, like a coin or a drum"), run: () => void ((params = { ...params, kind: "effect" }), renderGenerator()) }
  ]);
  byId("tk-gen-hint").textContent = params.kind === "tone" ? t("a melody sound is one tiny wave that repeats, so it plays any key and uses almost no memory") : t("a sound effect plays once at the pitch it was made, so longer ones use more memory");
  chips(byId("tk-gen-presets"), READY_MADE.filter((preset) => preset.kind === params.kind).map((preset) => ({
    label: t(preset.name),
    on: presetName === preset.name,
    hint: t(preset.hint),
    run: () => {
      params = paramsFor(preset);
      presetName = preset.name;
      renderGenerator();
    }
  })));
  chips(byId("tk-gen-shapes"), SHAPES.map((shape) => ({
    label: t(shape.label),
    on: params.shape === shape.id,
    run: () => void ((params = { ...params, shape: shape.id }), (presetName = ""), renderGenerator())
  })));
  const sliders = byId("tk-gen-sliders");
  sliders.replaceChildren();
  for (const spec of sliderSpec()) {
    if (spec.only && !spec.only(params)) continue;
    const scale = spec.key === "duty" ? 100 : 1;
    const row = slider(spec.label, spec.min, spec.max, Math.round(Number(params[spec.key]) * scale), spec.show, spec.step || 1);
    row.input.addEventListener("input", () => {
      params = { ...params, [spec.key]: Number(row.input.value) / scale };
      presetName = "";
      drawGenerator();
    });
    sliders.append(row.row);
  }
  drawGenerator();
}

function drawGenerator(): void {
  const made = synthesize(params);
  drawWave(byId<HTMLCanvasElement>("tk-gen-cv"), made.data);
  const bytes = brrBytes(Math.ceil(made.data.length / 16) * 16);
  byId("tk-gen-kb").textContent = t("about {kb} KB of the 58 KB", { kb: kb(bytes) });
}

function generatedSound(): TrackerSound {
  const made = synthesize(params);
  const shape = SHAPES.find((item) => item.id === params.shape)?.label || "sound";
  return soundFromMade(presetName ? t(presetName) : `${t(shape)} ${(state.song?.sounds.length || 0) + 1}`, made);
}

export function setupSounds(): void {
  const ear = byId<HTMLInputElement>("tk-ear");
  ear.checked = earCache;
  ear.addEventListener("change", () => {
    earCache = ear.checked;
    try {
      localStorage.setItem(EAR_KEY, earCache ? "1" : "0");
    } catch {
      // private windows can refuse storage, the choice just is not remembered
    }
  });
  byId("tk-make").addEventListener("click", () => {
    genOpen = !genOpen;
    byId("tk-readylist").hidden = true;
    renderGenerator();
  });
  byId("tk-gen-close").addEventListener("click", () => {
    genOpen = false;
    renderGenerator();
  });
  byId("tk-ready").addEventListener("click", () => {
    const list = byId("tk-readylist");
    list.hidden = !list.hidden;
    if (list.hidden) return;
    genOpen = false;
    renderGenerator();
    chips(list, READY_MADE.map((preset) => ({ label: `${t(preset.name)} · ${t(preset.hint)}`, on: false, run: () => addSound(readyMadeSound(preset)) })));
  });
  byId("tk-gen-play").addEventListener("click", () => previewNote(generatedSound(), 60, earCache));
  byId("tk-gen-rand").addEventListener("click", () => {
    params = surprise(Math.floor(Math.random() * 1e9));
    presetName = "";
    renderGenerator();
    previewNote(generatedSound(), 60, earCache);
  });
  byId("tk-gen-add").addEventListener("click", () => addSound(generatedSound()));
  byId<HTMLInputElement>("tk-wav").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = "";
    void importFiles(files);
  });
  const panel = byId("tk-p-sounds");
  panel.addEventListener("dragover", (event) => {
    if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
  });
  panel.addEventListener("drop", (event) => {
    if (!event.dataTransfer?.files.length) return;
    event.preventDefault();
    void importFiles(Array.from(event.dataTransfer.files));
  });
}
